import { useState } from "react";
import { ArrowLeft, Eye, EyeOff, Lock } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MatrixRainBackground from "@/components/auth/MatrixRainBackground";
import { apiClient } from "@/lib/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!password || !confirmPassword) {
      setErrorMessage("Veuillez remplir les deux champs.");
      return;
    }
    if (password.length < 8) {
      setErrorMessage("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage("Les mots de passe ne correspondent pas.");
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setErrorMessage(detail || "Une erreur est survenue.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="login-page-bg login-matrix-shell flex min-h-screen items-center justify-center px-4 py-8" data-testid="reset-password-page">
        <MatrixRainBackground />

        <div className="login-page-content w-full max-w-sm">
          <div className="login-card w-full rounded-lg p-8 text-center">
            <p className="mb-4 text-zinc-300">Lien invalide ou expiré.</p>
            <Link
              to="/forgot-password"
              className="text-sm text-[#4c7e55] dark:text-[#2e6d3b] underline hover:opacity-80"
              data-testid="request-new-link"
            >
              Demander un nouveau lien
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page-bg login-matrix-shell flex min-h-screen items-center justify-center px-4 py-8" data-testid="reset-password-page">
      <MatrixRainBackground />

      <div className="login-page-content w-full max-w-sm">
        <div className="mb-10 text-center">
          <img
            src="/logo-braux-white.png"
            alt="Braux Paysagistes"
            className="mx-auto h-20 w-auto"
          />
        </div>

        <div className="login-card w-full rounded-lg p-8">
          <Link
            to="/login"
            className="mb-6 inline-flex items-center gap-1.5 text-sm text-zinc-400 transition-colors hover:text-white"
            data-testid="back-to-login-from-reset"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour à la connexion
          </Link>

          <h1
            className="mb-2 font-['Playfair_Display'] text-2xl leading-tight text-white"
            data-testid="reset-password-title"
          >
            Nouveau mot de passe
          </h1>
          <p className="mb-6 text-sm text-zinc-400">
            Choisissez un nouveau mot de passe sécurisé (8 caractères min.).
          </p>

          {success ? (
            <div data-testid="reset-password-success">
              <div className="rounded-md border border-emerald-800/50 bg-emerald-900/30 p-4 text-sm text-emerald-300">
                Mot de passe mis à jour avec succès.
              </div>
              <Link
                to="/login"
                className="mt-4 block text-center text-sm text-[#4c7e55] dark:text-[#2e6d3b] underline hover:opacity-80"
                data-testid="go-to-login-after-reset"
              >
                Se connecter avec le nouveau mot de passe
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
              <div className="relative">
                <Lock className="pointer-events-none absolute left-4 top-4 h-6 w-6 text-zinc-500" />
                <Input
                  data-testid="reset-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Nouveau mot de passe"
                  autoComplete="new-password"
                  className="h-14 border-zinc-800 bg-zinc-900/50 pl-12 pr-14 text-white placeholder:text-zinc-600 focus-visible:border-white focus-visible:ring-0"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 transition-colors duration-200 hover:text-white"
                  data-testid="reset-password-visibility-toggle"
                  aria-label={showPassword ? "Masquer le nouveau mot de passe" : "Afficher le nouveau mot de passe"}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              <div className="relative">
                <Lock className="pointer-events-none absolute left-4 top-4 h-6 w-6 text-zinc-500" />
                <Input
                  data-testid="reset-password-confirm-input"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirmer le mot de passe"
                  autoComplete="new-password"
                  className="h-14 border-zinc-800 bg-zinc-900/50 pl-12 pr-14 text-white placeholder:text-zinc-600 focus-visible:border-white focus-visible:ring-0"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((value) => !value)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 transition-colors duration-200 hover:text-white"
                  data-testid="reset-password-confirm-visibility-toggle"
                  aria-label={showConfirmPassword ? "Masquer la confirmation du mot de passe" : "Afficher la confirmation du mot de passe"}
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              {errorMessage && (
                <p data-testid="reset-password-error" className="text-sm text-rose-400">
                  {errorMessage}
                </p>
              )}

              <Button
                data-testid="reset-password-submit-button"
                type="submit"
                disabled={isSubmitting}
                className="h-14 w-full rounded-sm bg-white text-base font-semibold text-black transition-transform duration-200 hover:scale-[1.01] hover:bg-zinc-200"
              >
                {isSubmitting ? "Mise à jour..." : "Mettre à jour le mot de passe"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
